import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useEffect, useState } from 'react';
import { useRevalidator, useRouteLoaderData } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { apiAdmin } from '@/api/admin';
import { ApiError } from '@/api/errors';
import type {
  EmailTemplateDto,
  EmailTemplateName,
  PlatformDefaultsDto,
  UpdateTenantRequest,
} from '@/api/types';
import { EMAIL_TEMPLATE_NAMES } from '@/api/types';
import { useCopy } from '@/hooks/useCopy';
import { useSnackbar } from '@/components/Snackbar';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ErrorBanner } from '@/components/ErrorBanner';
import type { TenantWorkspaceLoaderData } from './TenantWorkspace';

export function TenantEmailTemplates() {
  const data = useRouteLoaderData('tenant-workspace') as TenantWorkspaceLoaderData;
  const tenant = data.tenant;
  const { t } = useCopy();
  const snack = useSnackbar();
  const [rows, setRows] = useState<EmailTemplateDto[] | null>(null);
  const [editing, setEditing] = useState<EmailTemplateName | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<EmailTemplateDto | null>(null);
  const [busy, setBusy] = useState(false);

  async function refresh() {
    try {
      setRows(await apiAdmin.emailTemplates.list(tenant.id));
    } catch {
      setRows([]);
    }
  }

  useEffect(() => {
    // `refresh()` only setStates after an `await`; wrap it in an inline async
    // IIFE so the effect body holds no synchronous setState (behavior identical).
    void (async () => {
      await refresh();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant.id]);

  const slots = EMAIL_TEMPLATE_NAMES.map((slot) => ({
    slot,
    mapping: rows?.find((r) => r.templateName === slot) ?? null,
  }));

  async function handleRemove() {
    if (!confirmRemove) return;
    setBusy(true);
    try {
      await apiAdmin.emailTemplates.delete(tenant.id, confirmRemove.id);
      snack.success(t('snackbar.templateRemoved', ''));
      setConfirmRemove(null);
      await refresh();
    } catch (err) {
      if (err instanceof ApiError) snack.error(t(`errors.codes.${err.code}`, err.code));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Box>
      <EmailConfigForm />
      <Typography variant="h2" component="h2" gutterBottom>
        {t('emailTemplates.title', 'Email templates')}
      </Typography>
      {!tenant.emailTemplatesDir ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('emailTemplates.noDirHint', '')}
        </Alert>
      ) : null}
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('emailTemplates.slot', 'Slot')}</TableCell>
              <TableCell>{t('emailTemplates.filename', 'File name')}</TableCell>
              <TableCell align="right">{t('common.actions', 'Actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {slots.map(({ slot, mapping }) => (
              <TableRow key={slot}>
                <TableCell>{t(`emailTemplates.names.${slot}`, slot)}</TableCell>
                <TableCell>
                  {mapping ? (
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'monospace',
                      }}
                    >
                      {mapping.filename}
                    </Typography>
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                        fontStyle: 'italic',
                      }}
                    >
                      {t('emailTemplates.platformDefault', 'Platform default')}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      justifyContent: 'flex-end',
                    }}
                  >
                    <IconButton aria-label="Edit" onClick={() => setEditing(slot)}>
                      <EditIcon />
                    </IconButton>
                    {mapping ? (
                      <IconButton aria-label="Remove" onClick={() => setConfirmRemove(mapping)}>
                        <DeleteIcon />
                      </IconButton>
                    ) : null}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <UpsertEmailTemplateDialog
        open={editing !== null}
        tenantId={tenant.id}
        slot={editing}
        existing={editing && rows ? (rows.find((r) => r.templateName === editing) ?? null) : null}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          void refresh();
          snack.success(t('snackbar.templateSaved', ''));
        }}
      />
      <ConfirmDialog
        open={Boolean(confirmRemove)}
        title={t('emailTemplates.deleteTitle', 'Revert to default?')}
        body={t('emailTemplates.deleteBody', '', {
          slot: confirmRemove ? t(`emailTemplates.names.${confirmRemove.templateName}`, '') : '',
        })}
        confirmLabel={t('common.remove', 'Remove')}
        severity="warning"
        busy={busy}
        onConfirm={() => void handleRemove()}
        onCancel={() => setConfirmRemove(null)}
      />
    </Box>
  );
}

interface EmailConfigFormValues {
  emailTemplatesDir: string;
  emailFromName: string;
  emailFromAddress: string;
  emailSupportEmail: string;
}

/**
 * Email configuration moved here from the Settings tab (spec 0045). Saves via the
 * SAME tenant-update API (`apiAdmin.tenants.update`) as the Settings tab — it is the
 * same UpdateTenantRequest DTO, just split across two tabs. The platform-default
 * placeholder logic (spec 0037) and the SPF/DKIM caveat moved over verbatim.
 */
function EmailConfigForm() {
  const data = useRouteLoaderData('tenant-workspace') as TenantWorkspaceLoaderData;
  const tenant = data.tenant;
  const { t } = useCopy();
  const revalidator = useRevalidator();
  const snack = useSnackbar();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  // Spec 0037 — endpoint values shown as placeholders (the tenant fields are the only
  // override tier). Null until saas-auth 0038 → today's behavior (no placeholders).
  const [platform, setPlatform] = useState<PlatformDefaultsDto | null>(null);
  useEffect(() => {
    void apiAdmin.platformDefaults.get().then(setPlatform);
  }, []);
  const ph = (value: string | null | undefined) =>
    value
      ? {
          placeholder: t('tenants.settings.platformPlaceholder', 'platform default: {{value}}', {
            value,
          }),
        }
      : {};

  const form = useForm<EmailConfigFormValues>({
    defaultValues: {
      emailTemplatesDir: tenant.emailTemplatesDir ?? '',
      emailFromName: tenant.emailFromName ?? '',
      emailFromAddress: tenant.emailFromAddress ?? '',
      emailSupportEmail: tenant.emailSupportEmail ?? '',
    },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setPending(true);
    setError(null);
    // Split-save safety (spec 0045): the Settings tab owns name/signupMode. Compose a
    // FULL UpdateTenantRequest from the loaded tenant, overriding only the four email
    // fields, so name/signupMode are preserved regardless of backend merge semantics.
    const payload: UpdateTenantRequest = {
      name: tenant.name,
      signupMode: tenant.signupMode,
      emailTemplatesDir: values.emailTemplatesDir || null,
      emailFromName: values.emailFromName || null,
      emailFromAddress: values.emailFromAddress || null,
      emailSupportEmail: values.emailSupportEmail || null,
    };
    try {
      await apiAdmin.tenants.update(tenant.id, payload);
      snack.success(t('snackbar.tenantUpdated', 'Tenant updated.'));
      revalidator.revalidate();
    } catch (err) {
      if (err instanceof ApiError) setError(err);
      else snack.error(t('errors.generic', 'Something went wrong.'));
    } finally {
      setPending(false);
    }
  });

  return (
    <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
      <Typography variant="h2" component="h2" gutterBottom>
        {t('emailTemplates.configTitle', 'Email configuration')}
      </Typography>
      <ErrorBanner error={error} />
      <Box component="form" onSubmit={onSubmit} noValidate>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={t('tenants.settings.emailTemplatesDir', 'Email templates directory')}
              {...form.register('emailTemplatesDir', { maxLength: 500 })}
              helperText={t('common.optional', 'Optional')}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              label={t('tenants.settings.emailFromName', 'Email from name')}
              {...ph(platform?.emailFromName)}
              {...form.register('emailFromName', { maxLength: 255 })}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              label={t('tenants.settings.emailFromAddress', 'Email from address')}
              type="email"
              {...ph(platform?.emailFromAddress)}
              helperText={t(
                'tenants.settings.emailFromCaveat',
                "A custom From domain must be authenticated with the mail provider (SPF/DKIM) or recipients' DMARC will junk the mail — operator responsibility.",
              )}
              {...form.register('emailFromAddress', { maxLength: 255 })}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              label={t('tenants.settings.emailSupportEmail', 'Support email')}
              type="email"
              {...ph(platform?.supportEmail)}
              {...form.register('emailSupportEmail', { maxLength: 255 })}
            />
          </Grid>
        </Grid>
        <Stack
          direction="row"
          spacing={2}
          sx={{
            alignItems: 'center',
            mt: 3,
          }}
        >
          <Button
            type="submit"
            variant="contained"
            disabled={pending || !form.formState.isDirty}
            startIcon={pending ? <CircularProgress size={16} /> : null}
          >
            {t('common.save', 'Save')}
          </Button>
        </Stack>
      </Box>
    </Paper>
  );
}

interface UpsertProps {
  open: boolean;
  tenantId: number;
  slot: EmailTemplateName | null;
  existing: EmailTemplateDto | null;
  onClose: () => void;
  onSaved: () => void;
}

function UpsertEmailTemplateDialog({
  open,
  tenantId,
  slot,
  existing,
  onClose,
  onSaved,
}: UpsertProps) {
  const { t } = useCopy();
  const form = useForm<{ filename: string; templateName: EmailTemplateName }>({
    defaultValues: { filename: '', templateName: 'EMAIL_VERIFICATION' },
  });
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (open) {
      form.reset({
        filename: existing?.filename ?? '',
        templateName: slot ?? 'EMAIL_VERIFICATION',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, slot, existing]);

  const onSubmit = form.handleSubmit(async (values) => {
    setPending(true);
    try {
      await apiAdmin.emailTemplates.upsert(tenantId, {
        templateName: values.templateName,
        filename: values.filename,
      });
      onSaved();
    } finally {
      setPending(false);
    }
  });

  return (
    <Dialog open={open} onClose={pending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('emailTemplates.upsertTitle', 'Map template')}</DialogTitle>
      <DialogContent>
        <Box component="form" onSubmit={onSubmit} id="upsert-template-form" noValidate>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              fullWidth
              label={t('emailTemplates.slot', 'Slot')}
              {...form.register('templateName')}
              defaultValue={slot ?? 'EMAIL_VERIFICATION'}
              disabled
            >
              {EMAIL_TEMPLATE_NAMES.map((n) => (
                <MenuItem key={n} value={n}>
                  {t(`emailTemplates.names.${n}`, n)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              label={t('emailTemplates.filename', 'File name')}
              required
              {...form.register('filename', { required: true })}
              helperText={t('emailTemplates.fileMustExist', '')}
            />
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={pending}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button form="upsert-template-form" type="submit" variant="contained" disabled={pending}>
          {t('common.save', 'Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
